import { useCallback, useState } from 'react';

import { Nullable } from '~types/Nullable';
import { extractAxiosErrorInfo, isUploadValidationError } from '~utils/extractAxiosError';

/**
 * Widget-side half of the inline upload-error policy (the hook-side half is the
 * `isUploadValidationError` re-throw gate in the upload hooks): holds the
 * message for a backend file-validation rejection so the widget can render it
 * inline next to its upload control. `showUploadError` ignores every other
 * failure class — those were already toasted/Sentry-reported by the owning
 * hook before it re-threw.
 */
export const useInlineUploadError = () => {
  const [uploadError, setUploadError] = useState<Nullable<string>>(null);

  const clearUploadError = useCallback(() => setUploadError(null), []);

  const showUploadError = useCallback((error: unknown, fallbackMessage: string) => {
    if (!isUploadValidationError(error)) return;
    setUploadError(extractAxiosErrorInfo(error, fallbackMessage).message);
  }, []);

  return { uploadError, clearUploadError, showUploadError };
};
